'use client';
import clsx from 'clsx';

import { QUIZ_STEPS, QuizStepIndex } from './quiz-steps';

type QuizStepperProps = {
  step: QuizStepIndex;
  canGoTo: (step: QuizStepIndex) => boolean;
  onStepClick: (step: QuizStepIndex) => void;
};

export function QuizStepper({ step, canGoTo, onStepClick }: QuizStepperProps) {
  return (
    <nav aria-label='Questionnaire progress' className='px-4 pb-2'>
      <p className='text-xs font-medium pb-1' aria-live='polite'>
        Step {step + 1} of {QUIZ_STEPS.length}
      </p>
      <ol className='grid grid-cols-3 gap-2'>
        {QUIZ_STEPS.map(({ id, label }, i) => {
          const index = i as QuizStepIndex;
          const isActive = index === step;
          const isDone = index < step;
          const isEnabled = !isActive && canGoTo(index);

          return (
            <li key={id}>
              <button
                aria-current={isActive ? 'step' : undefined}
                className='flex min-h-11 w-full flex-col gap-1.5 text-left disabled:cursor-default'
                disabled={!isEnabled}
                onClick={() => onStepClick(index)}
                type='button'
              >
                <span
                  className={clsx(
                    'h-1.5 w-full rounded-full transition-colors',
                    isActive || isDone ? 'bg-secondary' : 'bg-secondary/15'
                  )}
                />
                <span
                  className={clsx('flex items-start gap-1.5 text-xs leading-tight', {
                    'font-semibold': isActive,
                    'opacity-60': !isActive && !isDone,
                    underline: isEnabled,
                  })}
                >
                  <span
                    className={clsx(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                      isActive || isDone
                        ? 'bg-secondary text-secondary-foreground'
                        : 'border border-secondary/40 text-primary-foreground'
                    )}
                  >
                    {index + 1}
                  </span>
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
